import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useRealtimeSync } from './hooks/useRealtimeSync';
import { ErrorBoundary } from './components/ErrorBoundary';
import AppShell from './layout/AppShell';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import DashboardPage from './pages/DashboardPage';
import InvoicesPage from './pages/InvoicesPage';
import InvoiceFormPage from './pages/InvoiceFormPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import LorryReceiptsPage from './pages/LorryReceiptsPage';
import LorryReceiptFormPage from './pages/LorryReceiptFormPage';
import PartiesPage from './pages/PartiesPage';
import ProductsPage from './pages/ProductsPage';
import PaymentsPage from './pages/PaymentsPage';
import PurchasesPage from './pages/PurchasesPage';
import ExpensesPage from './pages/ExpensesPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import UserManagementPage from './pages/UserManagementPage';
import TemplatesPage from './pages/TemplatesPage';
import FeedbackPage from './pages/FeedbackPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminClientsPage from './pages/admin/AdminClientsPage';
import AdminFeedbackPage from './pages/admin/AdminFeedbackPage';
import AdminLicenseKeysPage from './pages/admin/AdminLicenseKeysPage';
import AdminSubscriptionsPage from './pages/admin/AdminSubscriptionsPage';
import AdminImportsPage from './pages/admin/AdminImportsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import ManufacturingPage from './pages/ManufacturingPage';
import BomListPage from './pages/BomListPage';
import BomEditorPage from './pages/BomEditorPage';
import BankingPage from './pages/BankingPage';
import LoansCapitalPage from './pages/LoansCapitalPage';
import LoanAccountDetailPage from './pages/LoanAccountDetailPage';
import BankTransactionsPage from './pages/BankTransactionsPage';

function Protected({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isCheckingAuth, user } = useAuth();
  useRealtimeSync(user?.tenantId);
  // Wait for the async session-cookie check (AuthContext) to resolve before deciding to redirect —
  // otherwise a fresh browser context (new tab, or reopened after a restart) with no cached user
  // yet, but a still-valid session cookie, gets bounced to /login and never recovers.
  if (isCheckingAuth) return null;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

// Wraps each route in its own error boundary so a crash on one page
// doesn't blank out the entire app.
function Page({ children }: { children: React.ReactNode }) {
  return <ErrorBoundary>{children}</ErrorBoundary>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route
        path="/*"
        element={
          <Protected>
            <AppShell>
              <Routes>
                <Route index element={<Navigate to="/invoices" replace />} />
                <Route path="dashboard" element={<Page><DashboardPage /></Page>} />
                <Route path="invoices" element={<Page><InvoicesPage /></Page>} />
                <Route path="invoices/new" element={<Page><InvoiceFormPage /></Page>} />
                <Route path="invoices/:id" element={<Page><InvoiceDetailPage /></Page>} />
                <Route path="invoices/:id/edit" element={<Page><InvoiceFormPage /></Page>} />
                <Route path="lorry-receipts" element={<Page><LorryReceiptsPage /></Page>} />
                <Route path="lorry-receipts/new" element={<Page><LorryReceiptFormPage /></Page>} />
                <Route path="lorry-receipts/:id/edit" element={<Page><LorryReceiptFormPage /></Page>} />
                <Route path="parties" element={<Page><PartiesPage /></Page>} />
                <Route path="products" element={<Page><ProductsPage /></Page>} />
                <Route path="payments" element={<Page><PaymentsPage /></Page>} />
                <Route path="purchases" element={<Page><PurchasesPage /></Page>} />
                <Route path="expenses" element={<Page><ExpensesPage /></Page>} />
                <Route path="reports" element={<Page><ReportsPage /></Page>} />
                <Route path="banking" element={<Page><BankingPage /></Page>} />
                <Route path="banking/:id" element={<Page><BankTransactionsPage /></Page>} />
                <Route path="loans-capital" element={<Page><LoansCapitalPage /></Page>} />
                <Route path="loans-capital/loans/:id" element={<Page><LoanAccountDetailPage /></Page>} />
                <Route path="settings" element={<Page><SettingsPage /></Page>} />
                <Route path="users" element={<Page><UserManagementPage /></Page>} />
                <Route path="templates" element={<Page><TemplatesPage /></Page>} />
                <Route path="feedback" element={<Page><FeedbackPage /></Page>} />
                <Route path="admin/dashboard" element={<Page><AdminDashboardPage /></Page>} />
                <Route path="admin/clients" element={<Page><AdminClientsPage /></Page>} />
                <Route path="admin/license-keys" element={<Page><AdminLicenseKeysPage /></Page>} />
                <Route path="admin/subscriptions" element={<Page><AdminSubscriptionsPage /></Page>} />
                <Route path="admin/imports" element={<Page><AdminImportsPage /></Page>} />
                <Route path="admin/feedback" element={<Page><AdminFeedbackPage /></Page>} />
                <Route path="subscription" element={<Page><SubscriptionPage /></Page>} />
                <Route path="manufacturing" element={<Page><ManufacturingPage /></Page>} />
                <Route path="manufacturing/boms" element={<Page><BomListPage /></Page>} />
                <Route path="manufacturing/boms/new" element={<Page><BomEditorPage /></Page>} />
                <Route path="manufacturing/boms/:id" element={<Page><BomEditorPage /></Page>} />
              </Routes>
            </AppShell>
          </Protected>
        }
      />
    </Routes>
  );
}
